// Facets for the search results page, derived from the categories the
// returned items actually carry. The Square category tree tells us how those
// flat category names relate (root type -> game -> color/type/set -> value).

const IGNORED_ROOTS = new Set(['Reporting']);
const OTHER = 'Other';
const SINGLES = 'Singles';

// Maps a category name to every tree path it appears at, skipping the internal
// "Reporting" branch.
export const buildCategoryIndex = (tree) => {
  const index = new Map();
  const walk = (node, path) => {
    const here = [...path, node.name];
    if (!index.has(node.name)) index.set(node.name, []);
    index.get(node.name).push(here);
    (node.children || []).forEach((child) => walk(child, here));
  };
  (tree || []).filter((root) => !IGNORED_ROOTS.has(root.name)).forEach((root) => walk(root, []));
  return index;
};

const itemPaths = (item, index) =>
  (item.categories || []).flatMap((name) => index.get(name) || []);

// Product types (root categories) an item belongs to.
export const itemTypes = (item, index) => {
  const roots = [...new Set(itemPaths(item, index).map((p) => p[0]))];
  return roots.length ? roots : [OTHER];
};

// Normalises "Magic Sets" / "Pokemon Sets" to "Set".
const groupLabel = (name) => (/\bsets?$/i.test(name) ? 'Set' : name);

// The drill-down values an item offers once a product type is chosen:
// { dimension label: [values] }. For Singles that is Game plus the Color /
// Type / Set groups; for any other type it is the sub-category.
export const itemDimensions = (item, type, index) => {
  const dims = {};
  const add = (label, value) => {
    if (!dims[label]) dims[label] = new Set();
    dims[label].add(value);
  };
  itemPaths(item, index)
    .filter((p) => p[0] === type)
    .forEach((p) => {
      if (type === SINGLES) {
        if (p[1]) add('Game', p[1]);
        if (p.length >= 4) add(groupLabel(p[2]), p[3]);
      } else if (p[1]) {
        add('Category', p[1]);
      }
    });
  return dims;
};

const DIM_ORDER = ['Game', 'Category', 'Color', 'Type', 'Set'];
const dimRank = (label) => {
  const i = DIM_ORDER.indexOf(label);
  return i === -1 ? DIM_ORDER.length : i;
};

const sortedCounts = (counts) =>
  [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));

// filters = { type: string|null, dims: { [label]: value } }
// Returns the items passing the filters plus the facet lists to display.
export const applyFacets = (items, filters, index) => {
  const { type, dims = {} } = filters;
  const typeItems = type ? items.filter((it) => itemTypes(it, index).includes(type)) : items;

  const dimsByItem = new Map(typeItems.map((it) => [it, type ? itemDimensions(it, type, index) : {}]));
  const matches = (it, skipLabel) =>
    Object.entries(dims).every(([label, value]) =>
      label === skipLabel || dimsByItem.get(it)?.[label]?.has(value)
    );

  // Type counts ignore the type filter itself so every option stays visible.
  const typeCounts = new Map();
  items.forEach((it) => itemTypes(it, index).forEach((t) => typeCounts.set(t, (typeCounts.get(t) || 0) + 1)));

  // Each dimension is counted over items passing the *other* dimension filters.
  const labels = new Set();
  dimsByItem.forEach((d) => Object.keys(d).forEach((l) => labels.add(l)));
  // A dimension is offered when the type's results as a whole span 2+ values,
  // so picking a value in one dimension never hides the others.
  const dimensions = [...labels]
    .map((label) => {
      const all = new Set();
      typeItems.forEach((it) => dimsByItem.get(it)[label]?.forEach((v) => all.add(v)));
      const counts = new Map();
      typeItems.filter((it) => matches(it, label)).forEach((it) => {
        dimsByItem.get(it)[label]?.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
      });
      return { label, options: sortedCounts(counts), distinct: all.size };
    })
    .filter((d) => d.distinct > 1)
    .sort((a, b) => dimRank(a.label) - dimRank(b.label) || a.label.localeCompare(b.label));

  return {
    filtered: typeItems.filter((it) => matches(it)),
    types: sortedCounts(typeCounts),
    dimensions,
  };
};
